import { logError } from 'src/utils/logger';
import { getPageDimensions } from 'src/helpers';
import { isDataURL } from './dataUrl';
import { fixPageSvg } from './fixPageSvg';
import ProgressNotification from './progressNotification';
import { constants as config } from '@config';

export async function svgToCanvas (svg, params) {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  const {
    position = { x: 0, y: 0 },
    pageId,
    height,
    width,
    useTransparentBackground,
    quickMode = false,
    notify = true
  } = params;
  const progressNotification = new ProgressNotification('SVG to Image');

  progressNotification.numSteps = 3;
  notify && progressNotification.first();

  let pageSVG = svg;
  if (typeof svg === 'string') {
    const div = document.createElement('div');
    div.innerHTML = svg;
    pageSVG = div.firstChild;
  }

  if (useTransparentBackground) {
    const backgroundColour = pageSVG.querySelector(`#${config.background.color}`);
    if (backgroundColour) backgroundColour.setAttribute('fill', 'none');
    const backgroundImage = pageSVG.querySelector(`#${config.background.image}`);
    if (backgroundImage) backgroundImage.remove();
  }

  // Fonts and images need to be data URLs for them to work
  await fixPageSvg(pageSVG, pageId, quickMode);
  notify && progressNotification.next();

  const { sourceWidth, sourceHeight, destinationWidth, destinationHeight } = getPageDimensions(pageSVG, width, height);

  pageSVG.setAttribute('x', 0);
  pageSVG.setAttribute('y', 0);
  pageSVG.setAttribute('viewBox', `${position.x} ${position.y} ${sourceWidth} ${sourceHeight}`);
  pageSVG.style.width = destinationWidth;
  pageSVG.style.height = destinationHeight;
  canvas.width = destinationWidth;
  canvas.height = destinationHeight;

  const xml = new XMLSerializer().serializeToString(pageSVG);
  const blob = new Blob([xml], { type: 'image/svg+xml' });
  const b64Image = URL.createObjectURL(blob);
  notify && progressNotification.next();

  const img = await createImageFromBlobUrl(b64Image);

  // Clear canvas before reusing
  ctx.clearRect(0, 0, destinationWidth, destinationHeight);
  ctx.drawImage(img, 0, 0, img.width, img.height, 0, 0, destinationWidth, destinationHeight);

  return { canvas, b64Image, progressNotification, destinationWidth, destinationHeight };
}

export function createImageFromBlobUrl (b64Image) {
  return new Promise((resolve) => {
    const image = new Image();
    image.onload = async () => {
      try {
        await image.decode();
        await new Promise(resolve => INKIVE.Helpers.afterRendering(resolve));

        resolve(image);
      } catch (e) {
        logError('createImageFromBlobUrl')(e);
      }
    };
    image.src = b64Image;
  });
}

export default async function svgToImage (svg, params) {
  const {
    canvas,
    b64Image,
    progressNotification,
    destinationWidth,
    destinationHeight
  } = await svgToCanvas(svg, params);

  const {
    type = 'image/png',
    quality = 0.92,
    notify = true
  } = params;

  const imageUrl = canvas.toDataURL(type, quality).replace(type, 'image/octet-stream');
  if (!imageUrl || imageUrl === 'data:,' || !isDataURL(imageUrl)) {
    throw new Error('Downloading file failed. Data url is blank');
  }

  URL.revokeObjectURL(b64Image);
  notify && progressNotification.next();
  notify && progressNotification.last();

  INKIVE.C.DEBUG && console.log(`[svgToImage] ${destinationWidth}x${destinationHeight}px: ${imageUrl}`);

  return imageUrl;
}
